import React, { useMemo, useState } from 'react';
import type { Chef, Language, Recipe } from '../types';
import XIcon from './icons/XIcon';
import MergeIcon from './icons/MergeIcon';
import ChefAvatar from './ChefAvatar';
import { getTranslation } from '../utils/translations';
import { getChefDisplayName } from '../utils/chefDisplay';
import { previewChefMerge } from '../utils/chefMerge';
import { countRecipesForChef } from '../utils/chefMergeService';

interface ChefMergeModalProps {
    isOpen: boolean;
    onClose: () => void;
    chefs: Chef[];
    recipes: Recipe[];
    language?: Language;
    onMerge: (keepId: string, mergeId: string) => Promise<void>;
}

const ChefMergeModal: React.FC<ChefMergeModalProps> = ({
    isOpen,
    onClose,
    chefs,
    recipes,
    language = 'en',
    onMerge,
}) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [keepId, setKeepId] = useState('');
    const [mergeId, setMergeId] = useState('');
    const [merging, setMerging] = useState(false);
    const [error, setError] = useState('');

    const sortedChefs = useMemo(
        () => [...chefs].sort((a, b) => getChefDisplayName(a, language).localeCompare(getChefDisplayName(b, language))),
        [chefs, language],
    );

    const keepChef = keepId ? chefs.find((c) => c.id === keepId) ?? null : null;
    const mergeChef = mergeId ? chefs.find((c) => c.id === mergeId) ?? null : null;

    const preview = useMemo(() => {
        if (!keepChef || !mergeChef || keepChef.id === mergeChef.id) return null;
        return previewChefMerge(keepChef, mergeChef);
    }, [keepChef, mergeChef]);

    const mergeRecipeCount = mergeChef ? countRecipesForChef(recipes, mergeChef.id) : 0;

    if (!isOpen) return null;

    const handleMerge = async () => {
        if (!keepChef || !mergeChef || keepChef.id === mergeChef.id) return;
        setMerging(true);
        setError('');
        try {
            await onMerge(keepChef.id, mergeChef.id);
            setKeepId('');
            setMergeId('');
            onClose();
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : t('errorSaving'));
        } finally {
            setMerging(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[660] flex items-center justify-center p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-[2rem] shadow-2xl overflow-hidden border dark:border-slate-800 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b dark:border-slate-800 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-2">
                        <MergeIcon className="w-5 h-5 text-brand-sage" />
                        <h2 className="text-xl font-black text-slate-800 dark:text-slate-100">{t('mergeChefs')}</h2>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-full">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="overflow-y-auto p-6 space-y-4">
                    {error && (
                        <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded-xl text-sm font-bold">
                            {error}
                        </div>
                    )}

                    <p className="text-sm text-slate-500 dark:text-slate-400">{t('mergeChefsHint')}</p>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('mergeKeepChef')}</label>
                        <select
                            value={keepId}
                            onChange={(e) => setKeepId(e.target.value)}
                            className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                        >
                            <option value="">{t('selectChef')}</option>
                            {sortedChefs.map((chef) => (
                                <option key={chef.id} value={chef.id} disabled={chef.id === mergeId}>
                                    {getChefDisplayName(chef, language)} ({chef.nickname})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t('mergeRemoveChef')}</label>
                        <select
                            value={mergeId}
                            onChange={(e) => setMergeId(e.target.value)}
                            className="w-full p-3 border dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-sm"
                        >
                            <option value="">{t('selectChef')}</option>
                            {sortedChefs.map((chef) => (
                                <option key={chef.id} value={chef.id} disabled={chef.id === keepId}>
                                    {getChefDisplayName(chef, language)} ({chef.nickname})
                                </option>
                            ))}
                        </select>
                    </div>

                    {mergeChef && (
                        <p className="text-xs font-bold text-amber-600 dark:text-amber-400">
                            {t('mergeRecipesNotice').replace('{count}', String(mergeRecipeCount))}
                        </p>
                    )}

                    {preview && keepChef && mergeChef && (
                        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 space-y-3">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('mergePreview')}</p>
                            <div className="flex items-center gap-3">
                                <ChefAvatar imageUrl={preview.imageUrl} name={preview.name} size="md" variant="thumb" />
                                <div className="min-w-0">
                                    <p className="font-bold text-slate-800 dark:text-slate-100">{preview.name}</p>
                                    {preview.description && (
                                        <p className="text-xs text-slate-500 line-clamp-2">{preview.description}</p>
                                    )}
                                    <p className="text-[10px] text-slate-400 mt-1">@{preview.instagramUsername || preview.englishName}</p>
                                </div>
                            </div>
                            {preview.websites.length > 0 && (
                                <p className="text-[10px] text-slate-400 break-all">
                                    {preview.websites.join(' · ')}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-6 border-t dark:border-slate-800 flex gap-3 shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-xl text-xs uppercase tracking-widest"
                    >
                        {t('cancel')}
                    </button>
                    <button
                        onClick={() => void handleMerge()}
                        disabled={merging || !preview || !keepChef || !mergeChef}
                        className="flex-1 py-3 bg-brand-sage text-white font-black rounded-xl text-xs uppercase tracking-widest disabled:opacity-50"
                    >
                        {merging ? '...' : t('mergeConfirm')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChefMergeModal;
